import { useCallback, useMemo } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { useAtom, useAtomValue } from 'jotai';
import { useRouter } from 'next/router';

import { useAuth } from '@restworld/feature-authentication';
import { ModalConfirmContent } from '@restworld/ui-ds-base';

import { EntryComponentNew, CvErrorsFieldReInput } from './worker-entry-point.components';

import WorkerCVLoadingUI from './worker-cvloading-ui';
import { CoverLetterEntry } from '../cover-letter-application';
import {
  createdJobApplicationAtom,
  isCVUploadProcessCompletedAtom,
  spontaneousApplyDataAtom,
  uploadCVLoadingAtom,
  workerEntryPointErrorsAtom,
  workerIdAtom,
} from './worker-entry-point.atoms';
import { InAppAssessmentNewConfirmation } from './worker-entry-point.components/in-app-assessment-confirmation';
import { entryPointModalVisibleAtom } from './worker-entry-point-modal';
import { jobPositionApplyStatusAtom } from '../job-position/job-position.atoms';

export const EntryPointModalBody = ({ hideBackBtn }: { hideBackBtn?: boolean }) => {
  const [, toggleEntryPoint] = useAtom(entryPointModalVisibleAtom);
  const closeEntryPoint = useCallback(() => toggleEntryPoint(false), [toggleEntryPoint]);
  const [jobPositionApplyStatus] = useAtom(jobPositionApplyStatusAtom);

  const uploadCVLoading = useAtomValue(uploadCVLoadingAtom);
  const isCVUploadProcessCompleted = useAtomValue(isCVUploadProcessCompletedAtom);
  const errors = useAtomValue(workerEntryPointErrorsAtom);
  const workerId = useAtomValue(workerIdAtom);
  const spontaneousApplyData = useAtomValue(spontaneousApplyDataAtom);
  const createdJobApplication = useAtomValue(createdJobApplicationAtom);

  const { isAuthenticated } = useAuth();
  const router = useRouter();
  const { t } = useTranslation();
  const appliedForAJobEl = useMemo(
    () => (
      <ModalConfirmContent
        title={
          spontaneousApplyData?.didUserSpontaneousApply
            ? t('common:your_spontaneous_application_has_been_submitted')
            : t('common:application_delivered')
        }
        subtitle={
          spontaneousApplyData?.didUserSpontaneousApply
            ? t('common:the_selection_process_is_managed_by_the_company_you_applied_to_key')
            : t('common:confirm_if_everything_updated')
        }
        additionalContent={
          spontaneousApplyData?.didUserSpontaneousApply ? t('common:make_sure_your_profile_is_fully_up_to_date') : ''
        }
        primaryAction={{
          label: t('common:check_your_profile'),
          onClick() {
            router.push('/profilo');
          },
        }}
        secondaryAction={{
          label: t('common:close'),
          onClick() {
            closeEntryPoint();
          },
        }}
        icon={<CheckCircleIcon className="h-10 w-10 text-secondary-500" />}
      />
    ),
    [spontaneousApplyData?.didUserSpontaneousApply, t, router, closeEntryPoint]
  );

  const modalBody = useMemo(() => {
    if (errors?.statusCode && errors?.statusCode >= 400 && errors?.statusCode <= 409) return <CvErrorsFieldReInput />;
    else if (spontaneousApplyData?.isSpontaneousEntry && spontaneousApplyData?.showCoverLetter) {
      return spontaneousApplyData?.didUserSpontaneousApply ? (
        appliedForAJobEl
      ) : (
        <CoverLetterEntry
          workerId={workerId}
          restaurantID={spontaneousApplyData?.restaurantID}
          closeEntryPoint={closeEntryPoint}
          id={isAuthenticated ? 'authenticated-spontaneous-application' : 'unauthenticated-spontaneous-application'}
        />
      );
    } else if (uploadCVLoading || isCVUploadProcessCompleted) {
      // Modal A (Andrea's slide 6): an extra branch on this screen, never a replacement of it.
      // It is reached only once the CV flow has finished AND the application it created is
      // assessed in the app; the worker who is not in the rollout, and every application from
      // before it, falls through to the CV screen exactly as today.
      if (isCVUploadProcessCompleted && createdJobApplication?.assessment_channel === 'in_app')
        return <InAppAssessmentNewConfirmation />;
      return <WorkerCVLoadingUI workerId={workerId} isCompleted={isCVUploadProcessCompleted} />;
    } else if (isAuthenticated && jobPositionApplyStatus) return appliedForAJobEl;
    return <EntryComponentNew hideBackBtn={hideBackBtn} />;
  }, [
    appliedForAJobEl,
    closeEntryPoint,
    createdJobApplication?.assessment_channel,
    errors?.statusCode,
    hideBackBtn,
    isAuthenticated,
    isCVUploadProcessCompleted,
    jobPositionApplyStatus,
    spontaneousApplyData?.didUserSpontaneousApply,
    spontaneousApplyData?.isSpontaneousEntry,
    spontaneousApplyData?.restaurantID,
    spontaneousApplyData?.showCoverLetter,
    uploadCVLoading,
    workerId,
  ]);
  return modalBody;
};
